import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useClerk } from "@clerk/tanstack-react-start";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";

const PasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    password2: z.string().min(8, "Password must be at least 8 characters"),
  })
  .refine((d) => d.password === d.password2, {
    path: ["password2"],
    message: "Passwords do not match",
  });

type PasswordForm = z.infer<typeof PasswordSchema>;

const PasswordPageSearchSchema = z
  .object({
    message: z.string().optional(),
    redirectTo: z.literal("/summit/agenda").optional(),
  })
  .optional()
  .transform((value) => value ?? {});

type PasswordPageSearch = z.infer<typeof PasswordPageSearchSchema>;

export const Route = createFileRoute("/_user/settings/password/")({
  validateSearch: (search) => PasswordPageSearchSchema.parse(search),
  head: () =>
    createNoIndexMeta(
      "Set Password – Dream Coach Match",
      "Set or update your account password.",
    ),
  component: PasswordSettingsPage,
});

function PasswordSettingsPage() {
  const { user } = useClerk();
  const search: PasswordPageSearch = Route.useSearch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordForm>({
    resolver: zodResolver(PasswordSchema),
    defaultValues: { password: "", password2: "" },
  });

  const onSubmit = async (data: PasswordForm) => {
    if (!user) {
      setError("You must be signed in to update password");
      return;
    }
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    try {
      await user.updatePassword({
        newPassword: data.password,
      });
      reset();
      if (search.redirectTo) {
        if (typeof window !== "undefined") {
          window.location.replace(search.redirectTo);
        }
        return;
      }
      setSuccess("Your password has been updated.");
    } catch {
      setError("Failed to update password. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mx-auto mb-4 flex w-full max-w-screen-sm flex-col overflow-visible">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="flex flex-col p-8 hover:shadow">
          {search.message ? (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {search.message}
            </div>
          ) : null}
          <div className="mb-4">
            <h2 className="text-xl font-semibold">Set your password</h2>
            <p className="text-sm text-gray-600">
              Create or update your password for email-based sign in.
            </p>
          </div>

          <FormField
            fullWidth
            label="New password"
            error={errors.password?.message || error || undefined}
          >
            <Input
              {...register("password")}
              type="password"
              autoComplete="new-password"
            />
          </FormField>

          <FormField
            fullWidth
            label="Confirm password"
            error={errors.password2?.message || undefined}
          >
            <Input
              {...register("password2")}
              type="password"
              autoComplete="new-password"
            />
          </FormField>

          {success ? (
            <div className="mt-2 text-sm text-green-700">{success}</div>
          ) : null}

          <div className="mt-6">
            <Button
              type="submit"
              loading={isProcessing}
              disabled={isProcessing}
            >
              Save password
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
